// useCreditBanks — the band-shared writers/musicians BANKS that autocomplete credit rows on
// the Details section. A bank entry (name + PRO/IPI for writers, instruments for musicians) is reused across
// releases (server-side ILIKE `?q=` search + last_used_on recency). Built on useBandQuery/useBandMutation →
// keys ['band', bandId, …]. The GETs return a bare array; adds are server-confirm then repaint.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type WriterBankDto = components["schemas"]["WriterBankDto"];
type MusicianBankDto = components["schemas"]["MusicianBankDto"];
type WriterBankAddRequest = components["schemas"]["WriterBankAddRequest"];
type WriterBankUpdateRequest = components["schemas"]["WriterBankUpdateRequest"];
type MusicianBankAddRequest = components["schemas"]["MusicianBankAddRequest"];

export type { WriterBankDto, MusicianBankDto, WriterBankAddRequest, WriterBankUpdateRequest, MusicianBankAddRequest };

const writersBankKey = (bandId: string) => ["band", bandId, "writers-bank"] as const;
const musiciansBankKey = (bandId: string) => ["band", bandId, "musicians-bank"] as const;

/** `GET /bands/{bandId}/writers-bank?q=` — the band's writers bank (autocomplete for the split table). */
export function useWritersBank(q?: string) {
  const { api } = useApp();
  return useBandQuery<WriterBankDto[]>(
    ["writers-bank", q ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/writers-bank", {
        params: { path: { bandId }, query: { q } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 5 * 60_000 },
  );
}

/** `GET /bands/{bandId}/musicians-bank?q=` — the band's musicians bank (autocomplete for the musicians table). */
export function useMusiciansBank(q?: string) {
  const { api } = useApp();
  return useBandQuery<MusicianBankDto[]>(
    ["musicians-bank", q ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/musicians-bank", {
        params: { path: { bandId }, query: { q } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 5 * 60_000 },
  );
}

/** `POST /bands/{bandId}/writers-bank` — add a writer to the band bank (reused on the next release). */
export function useAddWriter() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<WriterBankDto, WriterBankAddRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/writers-bank", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: writersBankKey(bandId) }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't add the writer")),
    },
  );
}

/** `PATCH /bands/{bandId}/writers-bank/{writerId}` — edit a bank writer's PRO / IPI-CAE. The bank is
 *  band-shared, so the edit shows on every release that credits this writer. `null` leaves a field alone;
 *  an empty string clears it. */
export function useUpdateWriter() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<WriterBankDto, { writerId: string; body: WriterBankUpdateRequest }>(
    async (band, { writerId, body }) => {
      const { data, error, response } = await api.PATCH("/api/v1/bands/{bandId}/writers-bank/{writerId}", {
        params: { path: { bandId: band, writerId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: writersBankKey(bandId) }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't update the writer")),
    },
  );
}

/** `POST /bands/{bandId}/musicians-bank` — add a musician to the band bank (reused on the next release). */
export function useAddMusician() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<MusicianBankDto, MusicianBankAddRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/musicians-bank", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: musiciansBankKey(bandId) }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't add the musician")),
    },
  );
}
